import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
// New icons for maximize and minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';
const SCREEN_NAME = 'RFQ';

const RFQItemsViewModal = ({ open, onClose, rfqId }) => {
  const [activeTab, setActiveTab] = useState('RFQ Info');
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize

  // ----- Department Screen Config -----
  const [screenConfig, setScreenConfig] = useState(null);
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };
  const shouldShowDepartment = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };

  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  useEffect(() => {
    if (open && rfqId) {
      setLoading(true);
      fetch(`${API_URL}/rfqs/${rfqId}`)
        .then(res => res.json())
        .then(data => setRfq(data))
        .catch(() => setRfq(null))
        .finally(() => setLoading(false));
    }
  }, [open, rfqId]);

  useEffect(() => {
    if (open && rfqId) {
      (async () => {
        try {
          const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
          });
          if (!res.ok) {
            setScreenConfig(null);
            return;
          }
          const data = await res.json();
          setScreenConfig(Array.isArray(data) ? (data[0] || null) : data);
        } catch (e) {
          console.error('screen-config fetch error:', e);
          setScreenConfig(null);
        }
      })();
    }
  }, [open, rfqId]);

  if (!open) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'bg-yellow-500 text-white';
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'sent': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Build Department display: Name ID Code (supporting multiple field variants)
  const deptName = rfq?.DepartmentName || rfq?.department_name || rfq?.departmentName || '-';
  const deptId = rfq?.DepartmentID ?? rfq?.department_id ?? rfq?.departmentId ?? '-';
  const deptCode = rfq?.DepartmentCode || rfq?.department_code || rfq?.departmentCode || '-';
  const departmentLine = ` ${deptCode}`;

  const sections = [
    {
      title: 'RFQ Information',
      fields: [
        { label: 'RFQ No', value: rfq?.rfq_no },
        { label: 'RFQ Date', value: rfq?.rfq_date ? dayjs(rfq.rfq_date).format('YYYY-MM-DD') : '-' },
        { label: 'PR No', value: rfq?.pr_no || '-' },
        { label: 'Vendor Code', value: rfq?.vendor_code },
        { label: 'Vendor Name', value: rfq?.vendor_name },

        // Inject Department line according to screen-config flags
        ...(shouldShowDepartment(screenConfig)
          ? [{ label: 'Department', value: departmentLine }]
          : []),

        { 
          label: 'Status', 
          value: rfq?.status,
          render: (value) => (
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(value)}`}>
              {value}
            </span>
          )
        },
        { label: 'Delivery Terms', value: rfq?.delivery_terms || '-' },
        { label: 'Payment Terms', value: rfq?.payment_terms || '-' },
      ],
      name: 'RFQ Info'
    },
    {
      title: 'RFQ Items',
      fields: [
        {
          label: 'Items',
          value: '',
          render: () => (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-[#E2E8F0]">
                <thead>
                  <tr className="bg-[#F1F5F9] text-[#384959]">
                    <th className="p-2">#</th>
                    <th className="p-2">Item Code</th>
                    <th className="p-2">Description</th>
                    <th className="p-2">Qty</th>
                    <th className="p-2">UOM</th>
                    <th className="p-2">Unit Price</th>
                    <th className="p-2">Amount</th>
                    <th className="p-2">Disc %</th>
                    <th className="p-2">Discount</th>
                    <th className="p-2">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rfq?.items && rfq.items.length > 0 ? (
                    rfq.items.map((item, idx) => (
                      <tr key={idx} className="border-t border-[#E2E8F0]">
                        <td className="p-2 text-center">{idx + 1}</td>
                        <td className="p-2">{item.item_code}</td>
                        <td className="p-2">{item.description}</td>
                        <td className="p-2">{item.quantity}</td>
                        <td className="p-2">{item.unit_of_measure}</td>
                        <td className="p-2">{item.unit_price}</td>
                        <td className="p-2">{item.amount}</td>
                        <td className="p-2">{item.discount_percent}</td>
                        <td className="p-2">{item.discount}</td>
                        <td className="p-2">{item.net_amount}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center text-[#64748b] py-4">No items</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ),
          fullRow: true
        }
      ],
      name: 'Items'
    },
    {
      title: 'System Information',
      fields: [
        { 
          label: 'Created At', 
          value: rfq?.created_at ? dayjs(rfq.created_at).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { 
          label: 'Updated At', 
          value: rfq?.updated_at ? dayjs(rfq.updated_at).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { label: 'Created By', value: rfq?.created_by || '-' },
        { label: 'Updated By', value: rfq?.updated_by || '-' },
      ],
      name: 'System Info'
    }
  ];

  const currentSection = sections.find(section => section.name === activeTab) || sections[0];

  return (
  <div
    className={`fixed inset-0 z-50 flex justify-center items-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
  >
    <style>
      {`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}
    </style>

    {/* Overlay */}
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

    {/* Panel */}
    <div
      className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
        transition-transform transform-gpu duration-300 ease-in-out
        ${open ? 'translate-x-0' : 'translate-x-full'}`}
    >
      {/* Header */}
      <div className="flex-shrink-0 p-8 pb-6 border-b border-[#E2E8F0] flex justify-between items-center">
        <h2 className="text-2xl font-bold text-[#384959]">RFQ Details</h2>
        <div className="flex items-center gap-3">
          <button 
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
            onClick={toggleMaximize}
            title={isMaximized ? "Minimize" : "Maximize"}
          >
            {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
          </button>
          <button
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-grow overflow-y-auto scrollbar px-8 pb-6 space-y-6">
        {loading ? (
          <div className="bg-[#F9FAFB] rounded-xl p-8 text-center border border-[#E2E8F0]">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#6A89A7]"></div>
            </div>
            <p className="text-[#64748b]">Loading RFQ data...</p>
          </div>
        ) : rfq ? (
          sections.map((section, index) => (
            <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">{section.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field, fieldIndex) => (
                  <div key={fieldIndex} className={`flex flex-col ${field.fullRow ? 'md:col-span-2' : ''}`}>
                    <span className="text-sm font-medium text-[#64748b]">{field.label}</span>
                    <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal uppercase">
                      {field.render ? field.render(field.value) : field.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-[#F9FAFB] rounded-xl p-8 text-center border border-[#E2E8F0]">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-red-500 font-medium">RFQ not found</p>
            <p className="text-[#64748b] mt-2">The requested RFQ could not be loaded</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 p-8 pt-0 border-t border-[#E2E8F0] flex justify-end">
        <button
          type="button"
          className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

};
export default RFQItemsViewModal;